// Help pages — How to Use + FAQ

const styles = {
  page: { maxWidth: 760, margin: "0 auto", padding: "40px 24px 80px", color: "#fff", fontFamily: "'Poppins',sans-serif" },
  back: { background: "transparent", border: "none", color: "rgba(255,255,255,.4)", cursor: "pointer", fontSize: 14, marginBottom: 24, fontFamily: "'Poppins',sans-serif" },
  eyebrow: { color: "#FF2D78", fontSize: 11, fontWeight: 800, letterSpacing: 3, textTransform: "uppercase", marginBottom: 10 },
  h1: { fontSize: 32, fontWeight: 900, color: "#fff", letterSpacing: -1, marginBottom: 14 },
  sub: { fontSize: 15, color: "rgba(255,255,255,.55)", marginBottom: 32, lineHeight: 1.7 },
  h2: { fontSize: 20, fontWeight: 800, color: "#fff", marginTop: 36, marginBottom: 14 },
  h3: { fontSize: 15, fontWeight: 800, color: "#fff", marginTop: 20, marginBottom: 8 },
  p: { fontSize: 14, color: "rgba(255,255,255,.78)", lineHeight: 1.75, marginBottom: 14 },
  step: { display: "flex", gap: 14, marginBottom: 18, alignItems: "flex-start" },
  num: { flexShrink: 0, width: 30, height: 30, borderRadius: 8, background: "#FF2D78", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 14 },
  stepBody: { flex: 1, fontSize: 14, color: "rgba(255,255,255,.85)", lineHeight: 1.7 },
  card: { background: "rgba(255,255,255,.04)", border: "1.5px solid rgba(255,255,255,.07)", borderRadius: 14, padding: "18px 20px", marginBottom: 12 },
  cardTitle: { fontWeight: 800, color: "#fff", fontSize: 14, marginBottom: 6 },
  cardDesc: { fontSize: 13, color: "rgba(255,255,255,.65)", lineHeight: 1.6 },
  examples: { fontSize: 12, color: "rgba(255,255,255,.45)", fontStyle: "italic", marginTop: 6 },
  tip: { background: "rgba(0,200,232,.06)", border: "1px solid rgba(0,200,232,.25)", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "rgba(255,255,255,.82)", lineHeight: 1.7, marginBottom: 14 },
  warn: { background: "rgba(255,45,120,.06)", border: "1px solid rgba(255,45,120,.25)", borderRadius: 10, padding: "12px 16px", fontSize: 13, color: "rgba(255,255,255,.82)", lineHeight: 1.7, marginBottom: 14 },
  faq: { background: "rgba(255,255,255,.04)", border: "1.5px solid rgba(255,255,255,.07)", borderRadius: 14, padding: "18px 20px", marginBottom: 10 },
  q: { fontWeight: 800, color: "#fff", fontSize: 14, marginBottom: 8 },
  a: { fontSize: 13, color: "rgba(255,255,255,.72)", lineHeight: 1.7 },
  cta: { display: "inline-block", background: "#FF2D78", color: "#fff", padding: "12px 24px", borderRadius: 10, textDecoration: "none", fontWeight: 800, fontSize: 14, marginTop: 14 },
  link: { color: "#00C8E8", cursor: "pointer", fontWeight: 600 },
};

function Back({ onBack }) {
  return <button onClick={onBack} style={styles.back}>← Back</button>;
}

// ─────────────────────────────────────────────
// HOW TO USE
// ─────────────────────────────────────────────

export function HowToUse({ onBack, onGoHome }) {
  return (
    <div style={styles.page}>
      <Back onBack={onBack} />
      <div style={styles.eyebrow}>How to use ShiftMateRN</div>
      <h1 style={styles.h1}>Your first 90 seconds.</h1>
      <p style={styles.sub}>
        Eighteen AI tools built around the moments nurses actually need help. Here's how to get the most out of them.
      </p>

      <h2 style={styles.h2}>Get started in 3 steps</h2>

      <div style={styles.step}>
        <div style={styles.num}>1</div>
        <div style={styles.stepBody}>
          <strong>Pick a tool from the home page.</strong> Each tool is tuned for a specific moment — care plans, charting, EKG reads, NCLEX practice, burnout check-ins, etc. Tap any tool card to enter it.
        </div>
      </div>

      <div style={styles.step}>
        <div style={styles.num}>2</div>
        <div style={styles.stepBody}>
          <strong>Type a question — or attach an image.</strong> The placeholder text shows you exactly what to type. For things like EKG strips or chart entries, tap the 📎 paperclip icon and snap a photo.
        </div>
      </div>

      <div style={styles.step}>
        <div style={styles.num}>3</div>
        <div style={styles.stepBody}>
          <strong>Save, print, or move on.</strong> Each response has Copy, Save, and Print buttons. Saved responses follow you to any device. Free plan = 5 free sessions (one-time), Pro = unlimited.
        </div>
      </div>

      <h2 style={styles.h2}>The 24 tools, by what you'll use them for</h2>

      <h3 style={styles.h3}>🏥 At the bedside (Nurse Mode)</h3>
      <div style={styles.card}>
        <div style={styles.cardTitle}>Charting Coach, Med Safety Check, EKG Interpreter, Patient Prioritizer, Shift Report Generator, Drip Calculator, Incident Report Helper, Care Plan Builder</div>
        <div style={styles.cardDesc}>For the moments mid-shift when you need an answer in the next 90 seconds.</div>
        <div style={styles.examples}>Try: "Pt c/o SOB, hx CHF, lasix 40mg IVP — write me a clean note" · "Patient on dopamine 5 mcg/kg/min, 80kg, what's my drip rate"</div>
      </div>

      <h3 style={styles.h3}>📚 In school or studying (Student Mode)</h3>
      <div style={styles.card}>
        <div style={styles.cardTitle}>NCLEX Coach, Care Plan Builder, AI Coach (Big Sis)</div>
        <div style={styles.cardDesc}>NGN-style practice, complete care plans with rationale, and a coach who never sighs at your question.</div>
        <div style={styles.examples}>Try: "Give me a bow-tie NCLEX question on heart failure" · "Patient with sepsis — write me a complete care plan"</div>
      </div>

      <h3 style={styles.h3}>💕 For your career and your sanity (Both)</h3>
      <div style={styles.card}>
        <div style={styles.cardTitle}>SBAR Simulator, Critical Reasoning, Lab Interpreter, Delegation Coach, Burnout Check-In, Interview Prep, Scope of Practice, Abbreviation Decoder</div>
        <div style={styles.cardDesc}>The skills nursing school doesn't teach: SBARing under pressure, surviving a tough shift, navigating scope-of-practice gray zones.</div>
        <div style={styles.examples}>Try: "Coach me through calling Dr. Smith about a 2am potassium of 6.4" · "I just had my third bad shift this week"</div>
      </div>

      <h2 style={styles.h2}>Pro tips</h2>

      <div style={styles.tip}>
        <strong>📎 Attach images.</strong> EKG strip from a textbook, screenshot of lab values, photo of a med list, handwritten shift notes — the AI can read all of these. Just tap the paperclip icon. Crop out any patient identifiers first.
      </div>

      <div style={styles.tip}>
        <strong>🔖 Save the gold.</strong> When an answer hits, tap Save. It syncs to your account — log in on any device, your library is there. Print any saved response for shift reports or your study binder.
      </div>

      <div style={styles.tip}>
        <strong>🎯 Be specific.</strong> "What's atrial fibrillation" gets a textbook answer. "Pt is in afib RVR at 140, BP 90/60, what's my next move" gets a clinical answer. The more context, the better.
      </div>

      <div style={styles.warn}>
        <strong>⚠️ Never enter PHI.</strong> Don't type real patient names, MRNs, or DOBs. Describe scenarios in generic terms: "a 58-year-old female with CHF" rather than "Mrs. Smith." This protects you under HIPAA and your hospital's policies.
      </div>

      <h2 style={styles.h2}>Free vs Pro</h2>

      <p style={styles.p}>
        <strong>Free:</strong> 5 free AI sessions (one-time trial), 23 of 24 tools unlocked (EKG Interpreter is Pro-only), save and share responses. No credit card.
      </p>
      <p style={styles.p}>
        <strong>Pro ($12/month):</strong> Unlimited sessions, save library across devices, cancel any time. Less than a Friday coffee.
      </p>
      <p style={styles.p}>
        <strong>Founding Member ($97 lifetime):</strong> Lifetime unlimited access to all 24 tools + every future tool we build + the full RN101 library (RN101: bestseller how to survive your first year of nursing, Rapid Response Mode guide, Your Patient's Labs Are Talking to You ebook + workbook, 100 SBAR Scenarios, Critical Thinking Made Amazingly Easy book + workbook, all 3 EKG ebooks) + a bonus: Nurse's Cheat Code to Making More Money Without Working One Extra Shift. Never pay again.
      </p>

      <button onClick={onGoHome} style={styles.cta}>Take me to the tools →</button>
    </div>
  );
}

// ─────────────────────────────────────────────
// FAQ
// ─────────────────────────────────────────────

export function FAQ({ onBack, onShowDisclaimer, onShowTerms, onShowPrivacy }) {
  const Q = ({ q, children }) => (
    <div style={styles.faq}>
      <div style={styles.q}>{q}</div>
      <div style={styles.a}>{children}</div>
    </div>
  );

  return (
    <div style={styles.page}>
      <Back onBack={onBack} />
      <div style={styles.eyebrow}>Frequently asked questions</div>
      <h1 style={styles.h1}>The questions nurses ask.</h1>
      <div style={{ background: "rgba(0,200,232,.08)", border: "1.5px solid rgba(0,200,232,.3)", borderRadius: 12, padding: "14px 18px", marginBottom: 24, display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 18 }}>✉️</span>
        <span style={{ fontSize: 14, color: "rgba(255,255,255,.85)" }}>Questions? <a href="mailto:support@rn101hq.com" style={{ color: "#00C8E8", fontWeight: 700 }}>support@rn101hq.com</a> — real human, fast response — we respond within 24 hours.</span>
      </div>

      <h2 style={styles.h2}>About the product</h2>

      <Q q="Is ShiftMateRN medical advice?">
        No. ShiftMateRN is an educational and reference tool. Always defer to your facility's policies, your supervising clinician, and your own professional judgment. See our <span style={styles.link} onClick={onShowDisclaimer}>Medical Disclaimer</span> for the full picture.
      </Q>

      <Q q="Who is this app for?">
        Nursing students and new nurses (first 1-3 years). Anyone going through nursing school, prepping for NCLEX, or surviving their first year on the floor. Experienced nurses and educators are welcome too.
      </Q>

      <Q q="Why $12 when ChatGPT is $20?">
        Because ChatGPT is a generalist — it'll happily write a wedding toast next to a clinical question. ShiftMateRN is built specifically for nursing: every prompt, every tool, every response style is tuned for clinical accuracy and the way nurses think and document.
      </Q>

      <Q q="What's different about the tools vs. just asking ChatGPT?">
        Each tool has a specialized system prompt written by a 19-year cardiac nurse. The Charting Coach knows hospital documentation standards. The EKG Interpreter uses the 5-criteria approach. The Incident Report Helper uses risk-management language. The Burnout Check-In knows nursing's unique stressors. ChatGPT is a hammer. ShiftMateRN is a toolbox.
      </Q>

      <h2 style={styles.h2}>Account and billing</h2>

      <Q q="Do I need an account to try it?">
        No. You can use 5 free sessions without signing up. Sign up only if you want to save responses or upgrade to Pro.
      </Q>

      <Q q="How do I cancel my Pro subscription?">
        Email <a href="mailto:support@rn101hq.com" style={styles.link}>support@rn101hq.com</a> and we'll cancel within one business day. Cancellation takes effect at the end of your current billing period (no prorated refund for partial months).
      </Q>

      <Q q="What's included in the Founding Member bundle?">
        Lifetime Pro access, RN101: bestseller how to survive your first year of nursing, Rapid Response Mode guide, Your Patient's Labs Are Talking to You (ebook + workbook), 100 SBAR Scenarios (Stop Freezing. Start SBARing.), Critical Thinking Made Amazingly Easy (book + workbook), all 3 EKG ebooks, and a bonus — Nurse's Cheat Code to Making More Money Without Working One Extra Shift. One-time payment of $97. The bundle is non-refundable after 7 days or after digital products have been downloaded.
      </Q>

      <Q q="Is this one-time forever?">
        Founding Member pricing is $97 one time — lifetime access forever. After founding member spots are gone the price goes to $12/month ($144/year).
      </Q>

      <Q q="Is my payment information secure?">
        Yes. All payments are processed by Stripe. We never see, store, or have access to your card number.
      </Q>

      <h2 style={styles.h2}>Privacy and safety</h2>

      <Q q="Is my data private?">
        Yes. We don't sell, rent, or share your data. We don't use it to serve ads. The only third parties who see anything are the service providers we use to operate the app (Supabase for accounts, Stripe for payments, Anthropic for the AI, Resend for emails). See our <span style={styles.link} onClick={onShowPrivacy}>Privacy Policy</span>.
      </Q>

      <Q q="Can I type real patient information?">
        Please don't. Never enter real patient names, MRNs, dates of birth, or any identifying details. Always describe scenarios in generic, de-identified terms. This protects you under HIPAA and your facility's policies.
      </Q>

      <Q q="Will my hospital see what I ask?">
        No. Your inputs aren't shared with employers or anyone else. We're not affiliated with any hospital or health system.
      </Q>

      <h2 style={styles.h2}>Using the AI</h2>

      <Q q="How accurate is the AI?">
        Very capable for most things, but not infallible. It can make mistakes — especially on dosing, calculations, EKG reads, and edge-case scenarios. <strong>Always verify clinical answers</strong> against a primary source (Lexicomp, your facility protocol, the prescriber, your preceptor) before acting. Treat it like a smart-but-fallible study partner, not a clinical authority.
      </Q>

      <Q q="What AI model is behind it?">
        Claude by Anthropic — Sonnet for text questions and Opus when you attach an image (better visual reasoning). Both are top-tier general-purpose AI models. The difference between us and ChatGPT is the nursing-specific prompts and tools wrapped around the model.
      </Q>

      <Q q="What if the AI gives me a wrong answer?">
        It happens. Verify everything against a primary clinical source before acting. If the AI gives you something you suspect is wrong, please email us at support so we can review and improve the tool.
      </Q>

      <Q q="Why does every response say 'verify with your preceptor'?">
        Because every response should. AI is a starting point, not the final word. Your preceptor, charge nurse, or supervising clinician sees what the AI can't and is who you'll always defer to.
      </Q>

      <h2 style={styles.h2}>Technical</h2>

      <Q q="Does it work on my phone?">
        Yes. The web app works on any phone browser. You can also "install" it: on iPhone, tap the Share icon → "Add to Home Screen." On Android, tap the menu → "Install app." It opens fullscreen like a native app.
      </Q>

      <Q q="Does it work offline?">
        No. The AI runs on Anthropic's servers, so you need an internet connection to ask questions. Saved responses are viewable offline if you've already loaded them.
      </Q>

      <Q q="Is there an iOS or Android app in the App Store?">
        Not yet. The web app + home-screen install gives you the same experience without the App Store delays. We may add native apps later.
      </Q>

      <h2 style={styles.h2}>For International Nurses (IENs)</h2>

      <Q q="I am an internationally educated nurse — is this app for me?">
        Absolutely! We built 3 tools specifically for IENs — the IEN Translator, NCLEX for IENs, and American Charting Adapter. Select IEN mode from the toggle at the top of the app.
      </Q>

      <Q q="Can this help me pass the NCLEX as an internationally educated nurse?">
        Yes — our NCLEX for IENs tool is built specifically for nurses trained outside the US. It bridges the gap between your training and American testing style.
      </Q>

      <Q q="Can this help me understand American nursing documentation?">
        Yes — the American Charting Adapter tool transforms your notes into American-style charting that meets US legal and professional standards.
      </Q>

      <h2 style={styles.h2}>Support</h2>

      <Q q="How do I contact support?">
        Email us at <a href="mailto:support@rn101hq.com" style={styles.link}>support@rn101hq.com</a> — we respond within 24 hours.
      </Q>

      <Q q="What if the AI gives me wrong clinical information?">
        Use the thumbs down button on any response to flag it. Email <a href="mailto:support@rn101hq.com" style={styles.link}>support@rn101hq.com</a> with details. Always verify clinical information with current resources and your physician.
      </Q>

      <Q q="How do I cancel my subscription?">
        Email <a href="mailto:support@rn101hq.com" style={styles.link}>support@rn101hq.com</a> and we will cancel within one business day. You keep access until the end of your billing period.
      </Q>

      <h2 style={styles.h2}>Still stuck?</h2>
      <Q q="Email us.">
        <a href="mailto:support@rn101hq.com" style={styles.link}>support@rn101hq.com</a> — real human, fast response.
      </Q>
    </div>
  );
}
